// A hand-checked list of Bitcoin addresses worth looking at.
//
// On attribution: these labels are community attributions, taken from public
// explorer tags and reporting - and, where a note says so, from the owner's own
// published list, which is stronger but still not proof. Nobody signs a message
// proving they own an address, so treat every label as "widely believed" rather
// than confirmed. The UI says as much. Two things keep this honest:
//
//   1. `approxBtc` records what the address actually held when the entry was
//      last checked, so a label that has drifted becomes visible rather than
//      staying silently wrong. Re-check any entry with:
//        curl "https://blockchain.info/multiaddr?active=<address>"
//   2. Anything that cannot be corroborated on-chain is left out. Notably there
//      is no Strategy/MicroStrategy entry: their holdings sit with custodians
//      across many addresses and no canonical public address exists, so any
//      single address labelled that way would be wrong.
//
// What this list cannot do, measured rather than assumed: make the whale
// alerts' exchange-direction line fire. That was the reason to grow it, and it
// does not work. Over 60 blocks, the transfers above 250 BTC net involved 66
// distinct sending addresses and 8 distinct receiving addresses, and *not one
// of them appeared twice*. Exchange flows run through deposit and change
// addresses that are used once and swept, so there is no recurring set to
// curate. Following the common-input-ownership heuristic out from the Binance
// cold wallet finds 13 sibling addresses, every one of them holding nothing.
//
// Naming the direction of a large transfer needs attribution at cluster scale -
// a sourced dataset like GraphSense - not more hand-checked rows here. Growing
// this list is worth doing for the directory pages, which are made of these
// entries and are thin; it is not a route to better alerts.
//
// Balances checked 2026-09-15. Re-check them with:
//
//   node scripts/refresh-curated-balances.mjs
//
// which updates the figures below and this date, and says which entries moved
// far enough that the label is worth re-reading. It reads blockchain.info, and
// so should any manual check: indexers disagree about early chain edge cases by
// whole coins, so a figure taken from one source and compared against another
// reports drift that never happened.

// Exported, not just written in the comment above, because a date the reader
// cannot see does no work. The pages print it and compute how long ago it was.
// scripts/refresh-curated-balances.mjs rewrites this line and the comment
// together, so the two cannot drift.
export const BALANCES_CHECKED = "2026-09-15";

export const curatedWallets = [
  {
    address: "1A1zP1eP5QGefi2DMPTfTL5SLmv7DivfNa",
    label: "Genesis block",
    category: "Historic",
    note: "The coinbase output of block 0, mined by Satoshi in January 2009. Unspendable by a quirk of the original code; the balance keeps creeping up because people still send tribute payments to it.",
    approxBtc: 107.48,
    source: "Bitcoin block 0",
  },
  {
    address: "12cbQLTFMXRnSzktFkuoG3eHoMeFtpTu3S",
    label: "Block 9 coinbase — sender of the first Bitcoin payment",
    category: "Historic",
    note: "Mined on 9 January 2009 and still holding tribute payments. Eleven blocks later this address sent 10 BTC to another person and took 40 BTC back as change, which is the first time bitcoin moved between two people rather than to whoever mined it.",
    approxBtc: 18.44,
    source: "Bitcoin block 9 coinbase, spent in block 170",
  },
  {
    address: "1Q2TWHE3GMdB6BZKafqwxXtWAWgFt5Jvm3",
    label: "Recipient of the first Bitcoin payment",
    category: "Historic",
    note: "Received 10 BTC in block 170 on 12 January 2009, paid out of block 9's mining reward. Reporting attributes it to Hal Finney and Satoshi respectively, which is attribution rather than proof — but the payment itself is in the chain and can be read directly.",
    approxBtc: 0.000078,
    source: "Bitcoin block 170, tx f4184fc5…9e16",
  },
  {
    address: "17SkEw2md5avVNyYgj6RiXuQKNwkXaxFyQ",
    label: "The 10,000 BTC pizza",
    category: "Historic",
    note: "Received exactly 10,000 BTC on 22 May 2010, paid for two delivered pizzas in the first documented purchase of a physical good with bitcoin. Worth a few tens of dollars then; the address keeps a dust balance today from people sending tribute.",
    approxBtc: 0.0038,
    source: "Bitcoin block 57043, tx a1075db5…d48d",
  },
  {
    address: "bc1qa5wkgaew2dkv56kfvj49j0av5nml45x9ek9hz6",
    label: "US Government — Silk Road seizure",
    category: "Government",
    note: "Holds the ~69,370 BTC the DOJ seized in 2020 from the hacker known as Individual X, who had taken it from Silk Road. The balance still matches the seizure figure almost exactly.",
    approxBtc: 69370,
    source: "US DOJ 2020 forfeiture complaint",
  },
  {
    address: "34xp4vRoCGJym3xR7yCVPFHoCNxv4Twseo",
    label: "Binance — cold wallet",
    category: "Exchange",
    note: "The largest known Bitcoin address. Binance listed it among its own reserve addresses in November 2022, and every major explorer tags it as Binance's.",
    approxBtc: 248598,
    source: "Binance's “Our Commitment to Transparency” post, 10 November 2022",
  },
  {
    address: "3M219KR5vEneNb47ewrPfWyb5jQ2DjxRP6",
    label: "Binance — second cold wallet",
    category: "Exchange",
    note: "The other very large address attributed to Binance, usually reported alongside the first. Also on the list of reserve addresses Binance published in November 2022.",
    approxBtc: 219060,
    source: "Binance's “Our Commitment to Transparency” post, 10 November 2022",
  },
  {
    address: "bc1qgdjqv0av3q56jvd82tkdjpy7gdp9ut8tlqmgrpmv24sq90ecnvqqjwvw97",
    label: "Bitfinex — cold wallet",
    category: "Exchange",
    note: "Long-standing cold storage attributed to Bitfinex, and one of the largest addresses on the network.",
    approxBtc: 130010,
    source: "Public explorer tags",
  },
  {
    address: "3LYJfcfHPXYJreMsASk2jkn69LWEYKzexb",
    label: "Binance — reserve wallet",
    category: "Exchange",
    note: "One of the four Bitcoin addresses Binance published as its own in November 2022. Reporting ties it to BTCB, Binance's Bitcoin-pegged token, which the address was opened in 2019 to back.",
    approxBtc: 68200,
    source: "Binance's “Our Commitment to Transparency” post, 10 November 2022",
  },
  {
    address: "bc1qm34lsc65zpw79lxes69zkqmk6ee3ewf0j77s3h",
    label: "Binance — hot wallet",
    category: "Exchange",
    note: "A working wallet rather than cold storage, so it moves constantly. The best address here for watching real traffic — the recorded balance moved about 50% between two checks eight days apart, which is the point of recording it. Binance published it among its reserve addresses in November 2022; the post did not say which of them were hot, so that part is the explorers' label.",
    approxBtc: 15478,
    source: "Binance's “Our Commitment to Transparency” post, 10 November 2022; hot wallet label from public explorer tags",
  },
  {
    address: "1FeexV6bAHb8ybZjqQMjJrcCrHGW9sb6uF",
    label: "Unattributed whale",
    category: "Whales",
    note: "One of the largest addresses never convincingly tied to an exchange or institution. Reporting has linked it to the Mt. Gox era, but the attribution is not settled — it is here precisely because nobody knows.",
    approxBtc: 79957,
    source: "Unconfirmed",
  },
];

export const walletCategories = [
  "Historic",
  "Government",
  "Exchange",
  "Whales",
];

export const randomCuratedWallet = () =>
  curatedWallets[Math.floor(Math.random() * curatedWallets.length)];

// Address -> entry, for matching against the live transaction feed. Bitcoin
// addresses are case-sensitive (base58 in particular), so this matches exactly
// rather than normalising, which would risk false positives.
const byAddress = new Map(curatedWallets.map((w) => [w.address, w]));

export const findCuratedWallet = (address) => byAddress.get(address);

// Which curated wallets a transaction touches, and on which side. A wallet can
// appear on both sides of the same transaction (change outputs do this), in
// which case it is reported once as "moved".
export const matchCuratedWallets = (tx) => {
  const sent = new Set();
  const received = new Set();

  (tx.inputs || []).forEach((input) => {
    const hit = findCuratedWallet(input?.prev_out?.addr);
    if (hit) sent.add(hit.address);
  });

  (tx.out || []).forEach((output) => {
    const hit = findCuratedWallet(output?.addr);
    if (hit) received.add(hit.address);
  });

  const addresses = new Set([...sent, ...received]);

  return [...addresses].map((address) => {
    const wallet = byAddress.get(address);
    const isSender = sent.has(address);
    const isRecipient = received.has(address);
    return {
      address,
      label: wallet.label,
      // the caller needs to tell an exchange from a government seizure: coins
      // arriving at an exchange is a different event from coins arriving at a
      // cold wallet that never moves
      category: wallet.category,
      direction: isSender && isRecipient ? "moved" : isSender ? "sent" : "received",
    };
  });
};
